import type { MapRef } from "@vis.gl/react-maplibre";
import { MapProvider } from "@vis.gl/react-maplibre";
import type { Map as MaplibreMap } from "maplibre-gl";
import { useEffect, useRef } from "react";
import type { Coords } from "../../lib/types";
import { BaseMap } from "./BaseMap";
import { type ParticleConfig, UniformWindParticles } from "./UniformWindParticles";

interface SpotDetailMapProps {
  /** Spot location — map is centered here. */
  coords: Coords;
  /** Meteorological wind direction for the current hour (degrees, where wind is FROM). */
  windDirectionDeg: number | null;
  /** Wind speed for the current hour, in m/s. */
  windSpeedMs: number | null;
  /** Optional zoom override (default: 11 — close enough to see the spot's local geography). */
  zoom?: number;
  /** Pass-through className for the wrapping div. */
  className?: string;
  /** Optional particle-effect overrides. */
  particleConfig?: Partial<ParticleConfig>;
  /** Optional override for the basemap style URL. */
  mapStyle?: string;
  /** Keep the WebGL drawing buffer so the map can be rasterised to an image
   *  (used by the /dev/social PNG export). Off by default. */
  preserveDrawingBuffer?: boolean;
  /** Fires once the underlying MapLibre instance has loaded, handing back the
   *  raw map. The /dev/social export uses it to wait for a painted frame
   *  before rasterising the canvas. */
  onMapReady?: (map: MaplibreMap) => void;
}

/**
 * Static, zoomed-in map for a single spot's detail view.
 *
 * The map has no interactive controls — all panning/zooming is disabled.
 * A particle layer flows in the wind direction provided by the caller.
 * When the caller switches the selected forecast hour, just pass new
 * `windDirectionDeg` / `windSpeedMs` and the particles re-render.
 */
export function SpotDetailMap({
  coords,
  windDirectionDeg,
  windSpeedMs,
  zoom = 11,
  className,
  particleConfig,
  mapStyle,
  preserveDrawingBuffer = false,
  onMapReady,
}: SpotDetailMapProps) {
  // `initialViewState` is applied only on mount. The detail page stays
  // mounted across spot-to-spot navigation (only params change), so recenter
  // the live map imperatively whenever the spot's coords change — otherwise
  // the map stays parked on the previously-viewed spot.
  const mapRef = useRef<MapRef>(null);
  useEffect(() => {
    mapRef.current?.getMap().jumpTo({ center: [coords.lon, coords.lat], zoom });
  }, [coords.lon, coords.lat, zoom]);

  return (
    <MapProvider>
      <BaseMap
        ref={mapRef}
        {...(className !== undefined && { className })}
        initialLongitude={coords.lon}
        initialLatitude={coords.lat}
        initialZoom={zoom}
        staticMap
        preserveDrawingBuffer={preserveDrawingBuffer}
        {...(onMapReady !== undefined && {
          onLoad: () => {
            const m = mapRef.current?.getMap();
            if (m) onMapReady(m as unknown as MaplibreMap);
          },
        })}
        {...(mapStyle !== undefined && { mapStyle })}
      >
        <UniformWindParticles
          directionDeg={windDirectionDeg}
          speedMs={windSpeedMs}
          {...(particleConfig !== undefined && { config: particleConfig })}
        />
      </BaseMap>
    </MapProvider>
  );
}
